import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createPricingRuleThunk,
  updatePricingRuleThunk,
} from "../../../store/thunks/pricingThunks";

// Default form structure
const DEFAULT_FORM_DATA = {
  name: "",
  description: "",
  priority: 0,
  isActive: true,

  conditions: {
    productTypes: [],
    subTypes: [],
    categories: [],
    purityLevels: [],
    minWeight: "",
    maxWeight: "",
    collections: [],
    branchCodes: [],
    customerTypes: [],
    minQuantity: "",
    maxQuantity: "",
  },

  charges: {
    makingCharges: {
      type: "PERCENTAGE",
      value: "",
      slabs: [],
      tiers: [],
      minAmount: "",
      maxAmount: "",
    },
    wastageCharges: {
      type: "PERCENTAGE",
      value: "",
      includeStoneWeight: false,
    },
    stoneCharges: {
      type: "PER_CARAT",
      value: "",
      useMarketRate: false,
      marginOnMarketRate: "",
    },
    hallmarkCharges: {
      enabled: true,
      type: "FIXED",
      value: 50,
    },
    additionalCharges: [],
    gstCharges: {
      rate: 3,
      includesMaking: true,
      includesWastage: true,
      includesStones: false,
    },
  },

  gstConfig: {
    includesMaking: true,
    includesWastage: true,
    includesStones: false,
    customRate: "",
  },

  validFrom: new Date().toISOString().split("T")[0],
  validUntil: "",
};

export const usePricingRuleForm = (initialData = null, mode = "add") => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // ✅ FIXED: Start with empty form for all modes
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState("basic");
  const [isDataLoaded, setIsDataLoaded] = useState(mode === "add"); // ✅ Track if data is loaded

  // ✅ FIXED: Update form when initial data changes (for edit/clone mode)
  useEffect(() => {
    if ((mode === "edit" || mode === "clone") && initialData) {
      console.log("Loading data for mode:", mode, "Data:", initialData);
      const parsed = parsePricingRuleData(initialData, mode === "clone");
      setFormData(parsed);
      setIsDataLoaded(true); // ✅ Mark data as loaded
    }
  }, [initialData, mode]);

  // ✅ FIXED: Reset form when switching modes without data
  useEffect(() => {
    if (mode === "add" && !initialData) {
      setFormData(DEFAULT_FORM_DATA);
      setIsDataLoaded(true);
    }
  }, [mode, initialData]);

  // Handle simple field changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle nested fields (e.g., "conditions.minWeight")
    if (name.includes(".")) {
      const parts = name.split(".");
      setFormData((prev) => {
        const newData = { ...prev };
        let current = newData;
        for (let i = 0; i < parts.length - 1; i++) {
          current[parts[i]] = { ...current[parts[i]] };
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] =
          type === "checkbox" ? checked : value;
        return newData;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handle array changes (for multi-select)
  const handleArrayChange = (field, value) => {
    const parts = field.split(".");
    setFormData((prev) => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < parts.length - 1; i++) {
        current[parts[i]] = { ...current[parts[i]] };
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return newData;
    });
  };

  // Handle slab changes
  const handleSlabChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        makingCharges: {
          ...prev.charges.makingCharges,
          slabs: prev.charges.makingCharges.slabs.map((slab, i) =>
            i === index ? { ...slab, [field]: value } : slab
          ),
        },
      },
    }));
  };

  const handleAddSlab = () => {
    setFormData((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        makingCharges: {
          ...prev.charges.makingCharges,
          slabs: [
            ...prev.charges.makingCharges.slabs,
            {
              minWeight: "",
              maxWeight: "",
              value: "",
              type: "PERCENTAGE",
            },
          ],
        },
      },
    }));
  };

  const handleRemoveSlab = (index) => {
    setFormData((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        makingCharges: {
          ...prev.charges.makingCharges,
          slabs: prev.charges.makingCharges.slabs.filter((_, i) => i !== index),
        },
      },
    }));
  };

  // Handle tier changes
  const handleTierChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        makingCharges: {
          ...prev.charges.makingCharges,
          tiers: prev.charges.makingCharges.tiers.map((tier, i) =>
            i === index ? { ...tier, [field]: value } : tier
          ),
        },
      },
    }));
  };

  const handleAddTier = () => {
    setFormData((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        makingCharges: {
          ...prev.charges.makingCharges,
          tiers: [
            ...prev.charges.makingCharges.tiers,
            {
              minQuantity: "",
              maxQuantity: "",
              value: "",
            },
          ],
        },
      },
    }));
  };

  const handleRemoveTier = (index) => {
    setFormData((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        makingCharges: {
          ...prev.charges.makingCharges,
          tiers: prev.charges.makingCharges.tiers.filter((_, i) => i !== index),
        },
      },
    }));
  };

  // Handle additional charges
  const handleAdditionalChargeChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        additionalCharges: prev.charges.additionalCharges.map((charge, i) =>
          i === index ? { ...charge, [field]: value } : charge
        ),
      },
    }));
  };

  const handleAddAdditionalCharge = () => {
    setFormData((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        additionalCharges: [
          ...prev.charges.additionalCharges,
          {
            name: "",
            type: "PERCENTAGE",
            value: "",
            appliesTo: "TOTAL",
            isOptional: false,
          },
        ],
      },
    }));
  };

  const handleRemoveAdditionalCharge = (index) => {
    setFormData((prev) => ({
      ...prev,
      charges: {
        ...prev.charges,
        additionalCharges: prev.charges.additionalCharges.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Basic validations
    if (!formData.name || formData.name.trim().length < 3) {
      newErrors.name = "Name is required (min 3 characters)";
    }

    if (formData.priority === "" || formData.priority < 0) {
      newErrors.priority = "Priority must be 0 or greater";
    }

    // Validate weight range
    if (
      formData.conditions.minWeight &&
      formData.conditions.maxWeight &&
      parseFloat(formData.conditions.minWeight) >=
        parseFloat(formData.conditions.maxWeight)
    ) {
      newErrors["conditions.maxWeight"] =
        "Max weight must be greater than min weight";
    }

    // Validate quantity range
    if (
      formData.conditions.minQuantity &&
      formData.conditions.maxQuantity &&
      parseInt(formData.conditions.minQuantity) >=
        parseInt(formData.conditions.maxQuantity)
    ) {
      newErrors["conditions.maxQuantity"] =
        "Max quantity must be greater than min quantity";
    }

    // Validate making charges
    if (
      formData.charges.makingCharges.type !== "SLAB" &&
      formData.charges.makingCharges.type !== "TIERED" &&
      !formData.charges.makingCharges.value
    ) {
      newErrors["charges.makingCharges.value"] =
        "Making charge value is required";
    }

    // Validate slabs
    if (
      formData.charges.makingCharges.type === "SLAB" &&
      formData.charges.makingCharges.slabs.length === 0
    ) {
      newErrors["charges.makingCharges.slabs"] =
        "At least one slab is required for slab-based pricing";
    }

    // Validate tiers
    if (
      formData.charges.makingCharges.type === "TIERED" &&
      formData.charges.makingCharges.tiers.length === 0
    ) {
      newErrors["charges.makingCharges.tiers"] =
        "At least one tier is required for tiered pricing";
    }

    // Validate validity dates
    if (formData.validUntil && formData.validFrom >= formData.validUntil) {
      newErrors.validUntil = "End date must be after start date";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const submitData = cleanFormData(formData);

      if (mode === "add" || mode === "clone") {
        await dispatch(createPricingRuleThunk(submitData)).unwrap();
        toast.success(
          `Pricing rule ${mode === "clone" ? "cloned" : "created"} successfully`
        );
      } else {
        await dispatch(
          updatePricingRuleThunk({
            id: initialData._id,
            ruleData: submitData,
          })
        ).unwrap();
        toast.success("Pricing rule updated successfully");
      }

      navigate("/pricing-rules");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error || `Failed to ${mode} pricing rule`);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    submitting,
    activeSection,
    setActiveSection,
    isDataLoaded, // ✅ Export loading state
    handleChange,
    handleArrayChange,
    handleSlabChange,
    handleAddSlab,
    handleRemoveSlab,
    handleTierChange,
    handleAddTier,
    handleRemoveTier,
    handleAdditionalChargeChange,
    handleAddAdditionalCharge,
    handleRemoveAdditionalCharge,
    handleSubmit,
    validateForm,
  };
};

// ✅ FIXED: Helper function to parse data for edit/clone mode
function parsePricingRuleData(rule, isClone = false) {
  console.log("Parsing rule data. isClone:", isClone, "Rule:", rule);

  const parsed = {
    name: isClone ? `${rule.name} (Copy)` : rule.name || "",
    description: rule.description || "",
    priority: rule.priority ?? 0,
    isActive: isClone
      ? false
      : rule.isActive !== undefined
      ? rule.isActive
      : true,

    conditions: {
      productTypes: rule.conditions?.productTypes || [],
      subTypes: rule.conditions?.subTypes || [],
      categories: rule.conditions?.categories || [],
      purityLevels: rule.conditions?.purityLevels || [],
      minWeight: rule.conditions?.minWeight || "",
      maxWeight: rule.conditions?.maxWeight || "",
      collections: rule.conditions?.collections || [],
      branchCodes: rule.conditions?.branchCodes || [],
      customerTypes: rule.conditions?.customerTypes || [],
      minQuantity: rule.conditions?.minQuantity || "",
      maxQuantity: rule.conditions?.maxQuantity || "",
    },

    charges: {
      makingCharges: {
        type: rule.charges?.makingCharges?.type || "PERCENTAGE",
        value: rule.charges?.makingCharges?.value || "",
        slabs: rule.charges?.makingCharges?.slabs || [],
        tiers: rule.charges?.makingCharges?.tiers || [],
        minAmount: rule.charges?.makingCharges?.minAmount || "",
        maxAmount: rule.charges?.makingCharges?.maxAmount || "",
      },
      wastageCharges: {
        type: rule.charges?.wastageCharges?.type || "PERCENTAGE",
        value: rule.charges?.wastageCharges?.value || "",
        includeStoneWeight:
          rule.charges?.wastageCharges?.includeStoneWeight || false,
      },
      stoneCharges: {
        type: rule.charges?.stoneCharges?.type || "PER_CARAT",
        value: rule.charges?.stoneCharges?.value || "",
        useMarketRate: rule.charges?.stoneCharges?.useMarketRate || false,
        marginOnMarketRate:
          rule.charges?.stoneCharges?.marginOnMarketRate || "",
      },
      hallmarkCharges: {
        enabled: rule.charges?.hallmarkCharges?.enabled !== false,
        type: rule.charges?.hallmarkCharges?.type || "FIXED",
        value: rule.charges?.hallmarkCharges?.value || 50,
      },
      additionalCharges: rule.charges?.additionalCharges || [],
      gstCharges: {
        rate: rule.charges?.gstCharges?.rate || 3,
        includesMaking: rule.charges?.gstCharges?.includesMaking !== false,
        includesWastage: rule.charges?.gstCharges?.includesWastage !== false,
        includesStones: rule.charges?.gstCharges?.includesStones || false,
      },
    },

    gstConfig: {
      includesMaking: rule.gstConfig?.includesMaking !== false,
      includesWastage: rule.gstConfig?.includesWastage !== false,
      includesStones: rule.gstConfig?.includesStones || false,
      customRate: rule.gstConfig?.customRate || "",
    },

    validFrom: rule.validFrom
      ? new Date(rule.validFrom).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
    validUntil: rule.validUntil
      ? new Date(rule.validUntil).toISOString().split("T")[0]
      : "",
  };

  console.log("Parsed data:", parsed);
  return parsed;
}

// Helper function to clean form data before submission
function cleanFormData(formData) {
  const cleaned = { ...formData };

  // Convert string numbers to actual numbers
  if (cleaned.priority) cleaned.priority = parseInt(cleaned.priority);

  // Clean conditions
  if (cleaned.conditions.minWeight)
    cleaned.conditions.minWeight = parseFloat(cleaned.conditions.minWeight);
  if (cleaned.conditions.maxWeight)
    cleaned.conditions.maxWeight = parseFloat(cleaned.conditions.maxWeight);
  if (cleaned.conditions.minQuantity)
    cleaned.conditions.minQuantity = parseInt(cleaned.conditions.minQuantity);
  if (cleaned.conditions.maxQuantity)
    cleaned.conditions.maxQuantity = parseInt(cleaned.conditions.maxQuantity);

  // Clean charges
  if (cleaned.charges.makingCharges.value)
    cleaned.charges.makingCharges.value = parseFloat(
      cleaned.charges.makingCharges.value
    );
  if (cleaned.charges.makingCharges.minAmount)
    cleaned.charges.makingCharges.minAmount = parseFloat(
      cleaned.charges.makingCharges.minAmount
    );
  if (cleaned.charges.makingCharges.maxAmount)
    cleaned.charges.makingCharges.maxAmount = parseFloat(
      cleaned.charges.makingCharges.maxAmount
    );

  if (cleaned.charges.wastageCharges.value)
    cleaned.charges.wastageCharges.value = parseFloat(
      cleaned.charges.wastageCharges.value
    );

  if (cleaned.charges.stoneCharges.value)
    cleaned.charges.stoneCharges.value = parseFloat(
      cleaned.charges.stoneCharges.value
    );
  if (cleaned.charges.stoneCharges.marginOnMarketRate)
    cleaned.charges.stoneCharges.marginOnMarketRate = parseFloat(
      cleaned.charges.stoneCharges.marginOnMarketRate
    );

  if (cleaned.charges.hallmarkCharges.value)
    cleaned.charges.hallmarkCharges.value = parseFloat(
      cleaned.charges.hallmarkCharges.value
    );

  // Clean slabs
  cleaned.charges.makingCharges.slabs = cleaned.charges.makingCharges.slabs.map(
    (slab) => ({
      ...slab,
      minWeight: parseFloat(slab.minWeight) || 0,
      maxWeight: parseFloat(slab.maxWeight) || 0,
      value: parseFloat(slab.value) || 0,
    })
  );

  // Clean tiers
  cleaned.charges.makingCharges.tiers = cleaned.charges.makingCharges.tiers.map(
    (tier) => ({
      ...tier,
      minQuantity: parseInt(tier.minQuantity) || 0,
      maxQuantity: parseInt(tier.maxQuantity) || 0,
      value: parseFloat(tier.value) || 0,
    })
  );

  // Clean additional charges
  cleaned.charges.additionalCharges = cleaned.charges.additionalCharges.map(
    (charge) => ({
      ...charge,
      value: parseFloat(charge.value) || 0,
    })
  );

  // Clean GST charges
  if (cleaned.charges.gstCharges.rate) {
    cleaned.charges.gstCharges.rate = parseFloat(
      cleaned.charges.gstCharges.rate
    );
  }

  // Clean GST config
  if (cleaned.gstConfig.customRate)
    cleaned.gstConfig.customRate = parseFloat(cleaned.gstConfig.customRate);

  // Remove empty strings and undefined values
  Object.keys(cleaned).forEach((key) => {
    if (cleaned[key] === "" || cleaned[key] === undefined) {
      delete cleaned[key];
    }
  });

  return cleaned;
}
